import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Image as ImageIcon, Tag as TagIcon, Calendar as CalendarIcon } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { createBlog } from "@/services/blogApi";

export const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('blog');
  const [status, setStatus] = useState('draft');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('09:00');

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const base64ToFile = (base64, filename = "image.jpg") => {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];

  const bstr = atob(arr[1]);
  let n = bstr.length;

  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

  const handleSave = async (publishNow = false) => {
  try {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    // Generate slug from title
    const slug = title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

    const payload = {
      title,
      slug,
      body: content,
      excerpt,
      category,
      tags,
      author: "Admin Team", // Replace with logged-in user if available
      content_type: contentType,
      status: publishNow
        ? "Published"
        : status.charAt(0).toUpperCase() + status.slice(1),

      scheduled_for:
        status === "scheduled" && scheduleDate
          ? new Date(
              `${format(scheduleDate, "yyyy-MM-dd")}T${scheduleTime}:00`
            ).toISOString()
          : null,

      meta_title: metaTitle,
      meta_description: metaDescription,
    };

    const formData = new FormData();

    formData.append("payload", JSON.stringify(payload));


    if (featuredImage) {
      const file = base64ToFile(featuredImage);

      console.log(file);
      console.log(file instanceof File);

      formData.append(
        "featured_image",
        file
      );
    }

    await createBlog(formData);

    toast.success(
      publishNow
        ? "Blog published successfully"
        : "Blog saved successfully"
    );

    navigate("/dashboard/blog");
  } catch (err) {
    console.error(err);

    toast.error(
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      "Unable to save blog"
    );
  }
};

  const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
  const readingTime = Math.ceil(wordCount / 200);

  return (
    <div>
      <PageHeader
        title={id ? 'Edit Post' : 'New Post'}
        breadcrumbs={['Admin', 'Content', 'Blog & Resources', id ? 'Edit' : 'New']}
        actions={
          <Button variant="outline" onClick={() => navigate('/dashboard/blog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Posts
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Editor - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardContent className="p-6 space-y-6">
              <div>
                <Input
                  placeholder="Enter post title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-2xl font-semibold border-0 px-0 focus-visible:ring-0 placeholder:text-slate-400"
                />
              </div>

              <div>
                <Label>Excerpt</Label>
                <Textarea
                  placeholder="Write a brief excerpt..."
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                  className="mt-2"
                  maxLength={200}
                />
                <p className="text-xs text-slate-500 mt-1">{excerpt.length}/200 characters</p>
              </div>

              <div>
                <Label>Content</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={content}
                    onChange={setContent}
                    placeholder="Write your post content..."
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {wordCount} words · ~{readingTime} min read
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">SEO Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Meta Title</Label>
                <Input
                  placeholder="SEO title (60 chars max)"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  maxLength={60}
                  className="mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {metaTitle.length}/60 characters
                </p>
              </div>

              <div>
                <Label>Meta Description</Label>
                <Textarea
                  placeholder="SEO description (160 chars max)"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  maxLength={160}
                  rows={3}
                  className="mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {metaDescription.length}/160 characters
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-700 mb-1">Google Search Preview</p>
                <p className="text-sm text-blue-600 hover:underline cursor-pointer">
                  {metaTitle || title || 'Your Post Title'}
                </p>
                <p className="text-xs text-emerald-700">propfirmhouse.com/blog/post-slug</p>
                <p className="text-xs text-slate-600 mt-1">
                  {metaDescription || excerpt || 'Your post description will appear here...'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Publish Settings */}
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Publish</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Content Type</Label>
                <RadioGroup value={contentType} onValueChange={setContentType} className="mt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="blog" id="blog" />
                    <Label htmlFor="blog">Blog Post</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="resource" id="resource" />
                    <Label htmlFor="resource">Trading Resource</Label>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {status === 'scheduled' && (
                <div className="space-y-3">
                  <Label>Schedule Publication</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {scheduleDate ? format(scheduleDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={scheduleDate}
                        onSelect={setScheduleDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    Post will be published automatically at the scheduled time
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => handleSave(false)}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button className="flex-1" onClick={() => handleSave(true)}>
                  {status === 'scheduled' ? 'Schedule' : 'Publish'}
                </Button>
              </div>

              <Button variant="outline" className="w-full">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            </CardContent>
          </Card>

          {/* Organization */}
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Organization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {contentType === 'blog' ? (
                      <>
                        <SelectItem value="reviews">Reviews</SelectItem>
                        <SelectItem value="guides">Guides</SelectItem>
                        <SelectItem value="news">News</SelectItem>
                        <SelectItem value="comparisons">Comparisons</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="guides">Guides</SelectItem>
                        <SelectItem value="tools">Tools</SelectItem>
                        <SelectItem value="calculators">Calculators</SelectItem>
                        <SelectItem value="strategy">Strategy</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  Tags
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Add tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button size="sm" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Author</Label>
                <Select defaultValue="admin">
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin Team</SelectItem>
                    <SelectItem value="editor">Content Editor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Featured Image - Moved here, made smaller */}
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Featured Image
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-w-xs mx-auto">
                <ImageUpload
                  value={featuredImage}
                  onChange={setFeaturedImage}
                  aspectRatio="16:9"
                />
                <p className="text-xs text-slate-500 text-center mt-2">
                  Recommended: 1200x675px
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
